import React from "react";
import { Cigarette, Users, Volume2, Sparkles } from "lucide-react";
import { Card, Input, Toggle, PillGroup, Select, TimePicker } from "./FormComponents";

export const Step13 = ({ data, updateData, isLight, showValidation }) => {
  const handleChange = (field, value) => {
    updateData("houseRules", { ...data, [field]: value });
  };

  const handleNestedChange = (parent, field, value) => {
    updateData("houseRules", {
      ...data,
      [parent]: {
        ...data[parent],
        [field]: value
      }
    });
  };

  return (
    <div className="flex flex-col gap-8">
      <Card title="Smoking" icon={Cigarette} subtitle="Smoking policy for the property." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <PillGroup 
            label="Smoking policy" required
            options={["No Smoking Anywhere", "Balcony/Outdoors Only", "Allowed Everywhere"]} 
            value={data.smoking.policy} isLight={isLight}
            showValidation={showValidation}
            onChange={(val) => handleNestedChange("smoking", "policy", val)}
          />
          <Toggle 
            label="Is a violation fee charged?" 
            value={data.smoking.fee.charged} isLight={isLight}
            onChange={(val) => {
              const updates = { charged: val };
              if (!val) {
                updates.amount = "";
                updates.currency = "€";
              }
              updateData("houseRules", { ...data, smoking: { ...data.smoking, fee: { ...data.smoking.fee, ...updates } } });
            }}
          >
            <div className="flex flex-col gap-1.5">
              <label className={`text-[10px] uppercase tracking-[0.18em] font-bold ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>Violation Fee Amount <span className="text-red-500 font-bold">*</span></label>
              <div className="flex gap-3 items-start max-w-[240px]">
                <Input 
                  type="text"
                  inputMode="decimal"
                  placeholder="150" required
                  value={data.smoking.fee.amount} isLight={isLight}
                  showValidation={showValidation}
                  className="w-32"
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9.]/g, "");
                    const parts = val.split(".");
                    const filteredVal = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : val;
                    handleNestedChange("smoking", "fee", { ...data.smoking.fee, amount: filteredVal });
                  }}
                />
                <Select 
                  options={["€"]} className="w-20"
                  value={data.smoking.fee.currency} isLight={isLight}
                  hidePlaceholder
                  onChange={(e) => handleNestedChange("smoking", "fee", { ...data.smoking.fee, currency: e.target.value })}
                />
              </div>
            </div>
          </Toggle>
        </div>
      </Card>

      <Card title="Guests, Visitors & Pets" icon={Users} subtitle="Set expectations for visitors, pets, and events." isLight={isLight}>
        <div className="flex flex-col gap-8 max-w-2xl">
          <div className="flex flex-col gap-6">
            <Toggle 
              label="Are parties or events allowed?" 
              value={data.visitors.partiesAllowed} isLight={isLight} 
              onChange={(val) => handleNestedChange("visitors", "partiesAllowed", val)} 
            />
            <Toggle 
              label="Are unregistered visitors allowed?" 
              value={data.visitors.unregisteredAllowed} isLight={isLight} 
              onChange={(val) => handleNestedChange("visitors", "unregisteredAllowed", val)} 
            />
            <Toggle 
              label="Are pets allowed?" 
              value={data.pets.allowed} isLight={isLight} 
              onChange={(val) => handleNestedChange("pets", "allowed", val)} 
            />
          </div>
        </div>
      </Card>

      <Card title="Quiet Hours" icon={Volume2} subtitle="Quiet hours guests should follow during their stay." isLight={isLight}>
        <div className="flex flex-col gap-10">
          <div className="flex flex-col gap-4">
            <span className={`text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>Night quiet hours <span className="text-red-500 font-bold">*</span></span>
            <div className="flex gap-4">
              <TimePicker label="From" required value={data.quietHours.night.from} isLight={isLight} showValidation={showValidation} onChange={(val) => handleNestedChange("quietHours", "night", { ...data.quietHours.night, from: val })} />
              <TimePicker label="Until" required value={data.quietHours.night.until} isLight={isLight} showValidation={showValidation} onChange={(val) => handleNestedChange("quietHours", "night", { ...data.quietHours.night, until: val })} />
            </div>
          </div>

          <Toggle 
            label="Do afternoon quiet hours apply?" 
            value={data.quietHours.afternoon.apply} isLight={isLight}
            onChange={(val) => {
              const updates = { apply: val };
              if (!val) {
                updates.from = "";
                updates.until = "";
              } else {
                if (!data.quietHours.afternoon.from) updates.from = "15:00";
                if (!data.quietHours.afternoon.until) updates.until = "17:30";
              }
              updateData("houseRules", { ...data, quietHours: { ...data.quietHours, afternoon: { ...data.quietHours.afternoon, ...updates } } });
            }}
          >
            <div className="flex gap-4 mt-6">
              <TimePicker label="From" required value={data.quietHours.afternoon.from} isLight={isLight} showValidation={showValidation} onChange={(val) => handleNestedChange("quietHours", "afternoon", { ...data.quietHours.afternoon, from: val })} />
              <TimePicker label="Until" required value={data.quietHours.afternoon.until} isLight={isLight} showValidation={showValidation} onChange={(val) => handleNestedChange("quietHours", "afternoon", { ...data.quietHours.afternoon, until: val })} />
            </div>
          </Toggle>
        </div>
      </Card>

      <Card title="Cleaning & Trash" icon={Sparkles} subtitle="Cleaning services and trash disposal details." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-6">
            <Toggle 
              label="Is mid-stay cleaning available upon request?" 
              value={data.cleaning.midStayAvailable} isLight={isLight}
              onChange={(val) => {
                const updates = { midStayAvailable: val };
                if (!val) updates.midStayFee = { applicable: false, amount: "", currency: "€" };
                updateData("houseRules", { ...data, cleaning: { ...data.cleaning, ...updates } });
              }}
            >
              <Toggle 
                label="Is there a fee for mid-stay cleaning?" 
                value={data.cleaning.midStayFee?.applicable} isLight={isLight}
                onChange={(val) => {
                  const updates = { applicable: val };
                  if (!val) {
                    updates.amount = "";
                    updates.currency = "€";
                  }
                  updateData("houseRules", { 
                    ...data, 
                    cleaning: { 
                      ...data.cleaning, 
                      midStayFee: { ...data.cleaning.midStayFee, ...updates } 
                    } 
                  });
                }}
              >
                <div className="flex flex-col gap-1.5">
                  <label className={`text-[10px] uppercase tracking-[0.18em] font-bold ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>Cleaning Fee Amount <span className="text-red-500 font-bold">*</span></label>
                  <div className="flex gap-3 items-start max-w-[240px]">
                    <Input 
                      type="text"
                      inputMode="decimal"
                      placeholder="30" required
                      value={data.cleaning.midStayFee?.amount} isLight={isLight}
                      showValidation={showValidation}
                      className="w-32"
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.]/g, "");
                        const parts = val.split(".");
                        const filteredVal = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : val;
                        handleNestedChange("cleaning", "midStayFee", { ...data.cleaning.midStayFee, amount: filteredVal });
                      }}
                    />
                    <Select 
                      options={["€"]} className="w-20"
                      value={data.cleaning.midStayFee?.currency || "€"} isLight={isLight}
                      hidePlaceholder
                      onChange={(e) => handleNestedChange("cleaning", "midStayFee", { ...data.cleaning.midStayFee, currency: e.target.value })}
                    />
                  </div>
                </div>
              </Toggle>
            </Toggle>
            <Toggle 
              label="Is extra linen available upon request?" 
              value={data.cleaning.extraLinenAvailable} isLight={isLight}
              onChange={(val) => {
                const updates = { extraLinenAvailable: val };
                if (!val) updates.extraLinenFee = { applicable: false, amount: "", currency: "€" };
                updateData("houseRules", { ...data, cleaning: { ...data.cleaning, ...updates } });
              }}
            >
              <Toggle 
                label="Is there a fee for extra linen?" 
                value={data.cleaning.extraLinenFee?.applicable} isLight={isLight}
                onChange={(val) => {
                  const updates = { applicable: val };
                  if (!val) {
                    updates.amount = "";
                    updates.currency = "€";
                  }
                  updateData("houseRules", { 
                    ...data, 
                    cleaning: { 
                      ...data.cleaning, 
                      extraLinenFee: { ...data.cleaning.extraLinenFee, ...updates } 
                    } 
                  });
                }}
              >
                <div className="flex flex-col gap-1.5">
                  <label className={`text-[10px] uppercase tracking-[0.18em] font-bold ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>Extra Linen Fee Amount <span className="text-red-500 font-bold">*</span></label>
                  <div className="flex gap-3 items-start max-w-[240px]">
                    <Input 
                      type="text"
                      inputMode="decimal"
                      placeholder="15" required
                      value={data.cleaning.extraLinenFee?.amount} isLight={isLight}
                      showValidation={showValidation}
                      className="w-32"
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.]/g, "");
                        const parts = val.split(".");
                        const filteredVal = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : val;
                        handleNestedChange("cleaning", "extraLinenFee", { ...data.cleaning.extraLinenFee, amount: filteredVal });
                      }}
                    />
                    <Select 
                      options={["€"]} className="w-20"
                      value={data.cleaning.extraLinenFee?.currency || "€"} isLight={isLight}
                      hidePlaceholder
                      onChange={(e) => handleNestedChange("cleaning", "extraLinenFee", { ...data.cleaning.extraLinenFee, currency: e.target.value })}
                    />
                  </div>
                </div>
              </Toggle>
            </Toggle>
          </div>

          <div className={`h-px w-full ${isLight ? 'bg-neutral-100' : 'bg-white/5'}`} />

          <Input 
            label="Where can guests take out the trash?" 
            placeholder="Green bins outside the main entrance" required
            value={data.trashLocation} isLight={isLight}
            showValidation={showValidation}
            onChange={(e) => handleChange("trashLocation", e.target.value)}
          />
        </div>
      </Card>
    </div>
  );
};
